'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isBefore, addMonths, subMonths, startOfDay, parseISO } from 'date-fns';
import { fr, enUS, nl, es, it } from 'date-fns/locale';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
const LOCALES = {
  fr,
  en: enUS,
  nl,
  es,
  it
} as const;
const triggerCls = 'flex w-full items-center gap-2 px-4 py-3 text-base border rounded-lg bg-background text-left cursor-pointer transition-colors hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary';
type Props = {
  name: string;
  id?: string;
  locale?: string;
  required?: boolean;
  minDate?: Date;
  placeholder?: string;
  /** Controlled value as 'yyyy-MM-dd'. Omit for self-contained state. */
  value?: string;
  onValueChange?: (value: string) => void;
};

/** shadcn-style date picker: opens a month calendar on click, disables days
 * before `minDate`, and carries the chosen value (yyyy-MM-dd) in a hidden
 * required input so native validation + Netlify Forms capture still work. */
export function DatePickerField(t0) {
  const $ = _c(106);
  const {
    name,
    id,
    locale: t1,
    required: t2,
    minDate,
    placeholder,
    value,
    onValueChange
  } = t0;
  const locale = t1 === undefined ? "en" : t1;
  const required = t2 === undefined ? false : t2;
  const controlled = value !== undefined;
  const [internal, setInternal] = React.useState("");
  const strValue = controlled ? value as string : internal;
  let t3;
  if ($[0] !== controlled || $[1] !== onValueChange) {
    t3 = v => {
      if (!controlled) {
        setInternal(v);
      }
      onValueChange?.(v);
    };
    $[0] = controlled;
    $[1] = onValueChange;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const setValue = t3;
  const [open, setOpen] = React.useState(false);
  const df = LOCALES[locale as keyof typeof LOCALES] || enUS;
  let t4;
  if ($[3] !== strValue) {
    t4 = strValue ? parseISO(strValue) : undefined;
    $[3] = strValue;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const selected = t4;
  let t5;
  if ($[5] !== minDate || $[6] !== selected) {
    t5 = selected || minDate || new Date();
    $[5] = minDate;
    $[6] = selected;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [viewMonth, setViewMonth] = React.useState(t5);
  let t6;
  let t7;
  if ($[8] !== strValue) {
    t6 = () => {
      if (strValue) {
        setViewMonth(parseISO(strValue));
      }
    };
    t7 = [strValue];
    $[8] = strValue;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  React.useEffect(t6, t7);
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t8;
  let t9;
  if ($[11] !== df || $[12] !== minDate || $[13] !== open || $[14] !== placeholder || $[15] !== selected || $[16] !== setValue || $[17] !== strValue || $[18] !== viewMonth) {
    const min = minDate ? startOfDay(minDate) : undefined;
    const gridStart = startOfWeek(startOfMonth(viewMonth), {
      locale: df
    });
    const gridEnd = endOfWeek(endOfMonth(viewMonth), {
      locale: df
    });
    const days = eachDayOfInterval({
      start: gridStart,
      end: gridEnd
    });
    let t17;
    let t18;
    if ($[30] !== df || $[31] !== open || $[32] !== placeholder || $[33] !== selected || $[34] !== strValue || $[35] !== viewMonth) {
      const weekdays = eachDayOfInterval({
        start: startOfWeek(new Date(), {
          locale: df
        }),
        end: endOfWeek(new Date(), {
          locale: df
        })
      });
      T1 = Popover;
      t14 = open;
      t15 = setOpen;
      const t19 = !strValue && "text-muted-foreground";
      let t20;
      if ($[46] !== t19) {
        t20 = cn(triggerCls, t19);
        $[46] = t19;
        $[47] = t20;
      } else {
        t20 = $[47];
      }
      let t21;
      if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = <CalendarIcon className="size-5 shrink-0 text-muted-foreground" />;
        $[48] = t21;
      } else {
        t21 = $[48];
      }
      let t22;
      if ($[49] !== df || $[50] !== placeholder || $[51] !== selected) {
        t22 = selected ? format(selected, "EEEE d MMMM yyyy", {
          locale: df
        }) : placeholder || "\u2014";
        $[49] = df;
        $[50] = placeholder;
        $[51] = selected;
        $[52] = t22;
      } else {
        t22 = $[52];
      }
      let t23;
      if ($[53] !== t22) {
        t23 = <span className="flex-1">{t22}</span>;
        $[53] = t22;
        $[54] = t23;
      } else {
        t23 = $[54];
      }
      if ($[55] !== t20 || $[56] !== t23) {
        t16 = <PopoverTrigger asChild={true}><button type="button" className={t20}>{t21}{t23}</button></PopoverTrigger>;
        $[55] = t20;
        $[56] = t23;
        $[57] = t16;
      } else {
        t16 = $[57];
      }
      T0 = PopoverContent;
      t10 = "w-auto p-3";
      t11 = "start";
      let t24;
      if ($[58] !== viewMonth) {
        t24 = () => setViewMonth(subMonths(viewMonth, 1));
        $[58] = viewMonth;
        $[59] = t24;
      } else {
        t24 = $[59];
      }
      let t25;
      if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
        t25 = <ChevronLeft className="size-4" />;
        $[60] = t25;
      } else {
        t25 = $[60];
      }
      let t26;
      if ($[61] !== t24) {
        t26 = <button type="button" aria-label="Previous month" onClick={t24} className="rounded-md p-1.5 hover:bg-accent">{t25}</button>;
        $[61] = t24;
        $[62] = t26;
      } else {
        t26 = $[62];
      }
      let t27;
      if ($[63] !== df || $[64] !== viewMonth) {
        t27 = format(viewMonth, "MMMM yyyy", {
          locale: df
        });
        $[63] = df;
        $[64] = viewMonth;
        $[65] = t27;
      } else {
        t27 = $[65];
      }
      let t28;
      if ($[66] !== t27) {
        t28 = <span className="text-sm font-semibold capitalize">{t27}</span>;
        $[66] = t27;
        $[67] = t28;
      } else {
        t28 = $[67];
      }
      let t29;
      if ($[68] !== viewMonth) {
        t29 = () => setViewMonth(addMonths(viewMonth, 1));
        $[68] = viewMonth;
        $[69] = t29;
      } else {
        t29 = $[69];
      }
      let t30;
      if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
        t30 = <ChevronRight className="size-4" />;
        $[70] = t30;
      } else {
        t30 = $[70];
      }
      let t31;
      if ($[71] !== t29) {
        t31 = <button type="button" aria-label="Next month" onClick={t29} className="rounded-md p-1.5 hover:bg-accent">{t30}</button>;
        $[71] = t29;
        $[72] = t31;
      } else {
        t31 = $[72];
      }
      if ($[73] !== t26 || $[74] !== t28 || $[75] !== t31) {
        t12 = <div className="mb-2 flex items-center justify-between">{t26}{t28}{t31}</div>;
        $[73] = t26;
        $[74] = t28;
        $[75] = t31;
        $[76] = t12;
      } else {
        t12 = $[76];
      }
      t17 = "mb-1 grid grid-cols-7";
      let t32;
      if ($[77] !== df) {
        t32 = (d, i) => <div key={i} className="py-1 text-center text-xs font-medium text-muted-foreground">{format(d, "EEEEEE", {
            locale: df
          })}</div>;
        $[77] = df;
        $[78] = t32;
      } else {
        t32 = $[78];
      }
      t18 = weekdays.map(t32);
      $[30] = df;
      $[31] = open;
      $[32] = placeholder;
      $[33] = selected;
      $[34] = strValue;
      $[35] = viewMonth;
      $[36] = T0;
      $[37] = T1;
      $[38] = t10;
      $[39] = t11;
      $[40] = t12;
      $[41] = t14;
      $[42] = t15;
      $[43] = t16;
      $[44] = t17;
      $[45] = t18;
    } else {
      T0 = $[36];
      T1 = $[37];
      t10 = $[38];
      t11 = $[39];
      t12 = $[40];
      t14 = $[41];
      t15 = $[42];
      t16 = $[43];
      t17 = $[44];
      t18 = $[45];
    }
    if ($[79] !== t17 || $[80] !== t18) {
      t13 = <div className={t17}>{t18}</div>;
      $[79] = t17;
      $[80] = t18;
      $[81] = t13;
    } else {
      t13 = $[81];
    }
    t8 = "grid grid-cols-7 gap-0.5";
    t9 = days.map((d_0, i_0) => {
      const disabled = min ? isBefore(d_0, min) : false;
      const isSel = selected ? isSameDay(d_0, selected) : false;
      const outside = !isSameMonth(d_0, viewMonth);
      return <button key={i_0} type="button" disabled={disabled} onClick={() => {
        setValue(format(d_0, "yyyy-MM-dd"));
        setOpen(false);
      }} className={cn("size-9 rounded-md text-sm transition-colors", isSel ? "bg-primary font-semibold text-primary-foreground" : "hover:bg-accent", outside && !isSel && "text-muted-foreground/40", disabled && "cursor-not-allowed text-muted-foreground/30 line-through hover:bg-transparent")}>{format(d_0, "d")}</button>;
    });
    $[11] = df;
    $[12] = minDate;
    $[13] = open;
    $[14] = placeholder;
    $[15] = selected;
    $[16] = setValue;
    $[17] = strValue;
    $[18] = viewMonth;
    $[19] = T0;
    $[20] = T1;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
    $[28] = t8;
    $[29] = t9;
  } else {
    T0 = $[19];
    T1 = $[20];
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t13 = $[24];
    t14 = $[25];
    t15 = $[26];
    t16 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t17;
  if ($[82] !== t8 || $[83] !== t9) {
    t17 = <div className={t8}>{t9}</div>;
    $[82] = t8;
    $[83] = t9;
    $[84] = t17;
  } else {
    t17 = $[84];
  }
  let t18;
  if ($[85] !== T0 || $[86] !== t10 || $[87] !== t11 || $[88] !== t12 || $[89] !== t13 || $[90] !== t17) {
    t18 = <T0 className={t10} align={t11}>{t12}{t13}{t17}</T0>;
    $[85] = T0;
    $[86] = t10;
    $[87] = t11;
    $[88] = t12;
    $[89] = t13;
    $[90] = t17;
    $[91] = t18;
  } else {
    t18 = $[91];
  }
  let t19;
  if ($[92] !== T1 || $[93] !== t14 || $[94] !== t15 || $[95] !== t16 || $[96] !== t18) {
    t19 = <T1 open={t14} onOpenChange={t15}>{t16}{t18}</T1>;
    $[92] = T1;
    $[93] = t14;
    $[94] = t15;
    $[95] = t16;
    $[96] = t18;
    $[97] = t19;
  } else {
    t19 = $[97];
  }
  let t20;
  if ($[98] !== id || $[99] !== name || $[100] !== required || $[101] !== strValue) {
    t20 = <input type="text" name={name} id={id} value={strValue} required={required} onChange={_temp} tabIndex={-1} aria-hidden={true} className="sr-only" />;
    $[98] = id;
    $[99] = name;
    $[100] = required;
    $[101] = strValue;
    $[102] = t20;
  } else {
    t20 = $[102];
  }
  let t21;
  if ($[103] !== t19 || $[104] !== t20) {
    t21 = <>{t19}{t20}</>;
    $[103] = t19;
    $[104] = t20;
    $[105] = t21;
  } else {
    t21 = $[105];
  }
  return t21;
}
function _temp() {}